import { formatFideliteDuplicataBlock } from './caisseTicketFideliteDisplay.js';
import { buildCommercantTicketIdentity } from './caisseDuplicataMerchant.js';
import {
  buildDuplicataRefundSynthesis,
  buildDuplicataReturnedArticles,
} from './caisseDuplicataRefundDisplay.js';

export function escapeDuplicataHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const PRINT_BODY_CLASS = 'caisse-duplicata-printing';

/**
 * Modèle d'aperçu React — pas d'HTML brut côté UI.
 */
export function buildDuplicataPreviewModel(ticket, display, formatMoney, commercant = null) {
  const { lines: merchantLines } = buildCommercantTicketIdentity(commercant);
  const numero = ticket?.numeroTicket || ticket?.numero || '';
  const dateLabel = ticket?.dateCommande
    ? new Date(ticket.dateCommande).toLocaleString('fr-FR')
    : '';
  const lignes = (ticket?.produits || []).map((lp) => ({
    id: lp.id,
    nom: lp.produit?.nom || 'Article',
    quantite: lp.quantite,
    montant: formatMoney(lp.prixTotal || lp.prixUnitaire * lp.quantite),
  }));
  const paiements = (ticket?.paiements || []).map((p, i) => ({
    id: p.id || `p-${i}`,
    methode: p.methodePaiement,
    montant: formatMoney(p.montant),
    montantRaw: Number(p.montant),
  }));
  const fideliteLines = (display?.lines || []).filter((l) => !String(l).startsWith('Total :'));
  const annule = ticket?.statut === 'ANNULEE';
  const synthesisRaw = buildDuplicataRefundSynthesis(ticket, ticket?.paiements);
  const returnedRaw = buildDuplicataReturnedArticles(ticket);
  const refundSynthesis = synthesisRaw.show
    ? {
        venteInitialeLabel: formatMoney(synthesisRaw.venteInitiale),
        remboursementsLabel: formatMoney(-synthesisRaw.totalRemboursements),
        netLabel: formatMoney(synthesisRaw.netApresRemboursements),
      }
    : null;
  const articlesRetournes = returnedRaw.rows.map((r) => ({
    ...r,
    montantRetourLabel: formatMoney(r.montantRetourTtc),
  }));
  return {
    merchantLines,
    numero,
    dateLabel,
    lignes,
    paiements,
    fideliteLines,
    totalLabel: formatMoney(ticket?.total),
    annule,
    footerMerci: annule ? '*** ANNULÉ ***' : 'Merci de votre visite !',
    refundSynthesis,
    articlesRetournes,
    showArticlesRetournes: articlesRetournes.length > 0,
    articlesRetournesDisclaimer:
      'Information de service — ne remplace pas un avoir fiscal. Ticket d’origine inchangé.',
  };
}

function renderRefundSynthesisHtml(model) {
  if (!model.refundSynthesis) return '';
  const s = model.refundSynthesis;
  return (
    `<div style="margin-top:10px;border-top:1px dashed #666;padding-top:8px;font-size:12px">` +
    `<p style="font-weight:bold;margin:0 0 6px">Synthèse remboursements (information)</p>` +
    `<div style="display:flex;justify-content:space-between"><span>Vente initiale</span><span>${escapeDuplicataHtml(s.venteInitialeLabel)}</span></div>` +
    `<div style="display:flex;justify-content:space-between"><span>Remboursements</span><span>${escapeDuplicataHtml(s.remboursementsLabel)}</span></div>` +
    `<div style="display:flex;justify-content:space-between;font-weight:bold"><span>Net après remboursements</span><span>${escapeDuplicataHtml(s.netLabel)}</span></div>` +
    `<p style="font-size:10px;margin:6px 0 0;color:#666">Duplicata — pas un avoir officiel (L3-S4).</p></div>`
  );
}

function renderArticlesRetournesHtml(model) {
  if (!model.showArticlesRetournes) return '';
  const rows = model.articlesRetournes
    .map(
      (r) =>
        `<tr><td>${escapeDuplicataHtml(r.nom)}</td>` +
        `<td style="text-align:center">vendu ${escapeDuplicataHtml(r.quantiteVendue)} / ret. ${escapeDuplicataHtml(r.quantiteRetournee)} / reste ${escapeDuplicataHtml(r.quantiteConservee)}</td>` +
        `<td style="text-align:right">${escapeDuplicataHtml(r.montantRetourLabel)}</td></tr>`
    )
    .join('');
  return (
    `<div style="margin-top:10px;border-top:1px dashed #999;padding-top:8px">` +
    `<p style="font-weight:bold;margin:0 0 4px">Articles retournés — information</p>` +
    `<table>${rows}</table>` +
    `<p style="font-size:10px;margin:4px 0 0;color:#666">${escapeDuplicataHtml(model.articlesRetournesDisclaimer)}</p></div>`
  );
}

/** Document HTML d'impression (legacy / tests) — contenu fiscal inchangé, champs échappés. */
export function buildDuplicataPrintHtml(ticket, display, formatMoney, commercant = null) {
  const model = buildDuplicataPreviewModel(ticket, display, formatMoney, commercant);
  const fideliteBlock = formatFideliteDuplicataBlock(display);
  const merchantBlock = model.merchantLines
    .map((l) => `<div style="text-align:center">${escapeDuplicataHtml(l)}</div>`)
    .join('');
  const numero = escapeDuplicataHtml(model.numero);
  const lignes = model.lignes
    .map(
      (lp) =>
        `<tr><td>${escapeDuplicataHtml(lp.nom)}</td>` +
        `<td style="text-align:center">x${escapeDuplicataHtml(lp.quantite)}</td>` +
        `<td style="text-align:right">${escapeDuplicataHtml(lp.montant)}</td></tr>`
    )
    .join('');
  const paiements = model.paiements
    .map(
      (p) =>
        `<div style="display:flex;justify-content:space-between"><span>${escapeDuplicataHtml(
          p.methode
        )}</span><span>${escapeDuplicataHtml(p.montant)}</span></div>`
    )
    .join('');
  const dateStr = escapeDuplicataHtml(model.dateLabel);
  const totalStr = escapeDuplicataHtml(model.totalLabel);
  const footer = escapeDuplicataHtml(model.footerMerci);

  return `<!DOCTYPE html><html><head><title>Ticket ${numero}</title>
      <style>body{font-family:monospace;max-width:350px;margin:0 auto;padding:20px;font-size:13px}
      h2{text-align:center;margin:0 0 10px}table{width:100%;border-collapse:collapse}
      td{padding:4px 0;border-bottom:1px dashed #ccc}.total{font-size:16px;font-weight:bold;text-align:center;margin:15px 0;border-top:2px solid #000;padding-top:10px}
      .dup{text-align:center;font-weight:bold;color:#c00;margin-bottom:10px}
      .merchant{text-align:center;margin-bottom:12px;font-size:12px;line-height:1.35}
      @media print{.no-print{display:none}}</style></head>
      <body>${merchantBlock ? `<div class="merchant">${merchantBlock}</div>` : ''}
      <p class="dup">--- DUPLICATA ---</p>
      <h2>Ticket ${numero}</h2>
      <p style="text-align:center">${dateStr}</p>
      <table>${lignes}</table>
      ${fideliteBlock}
      ${paiements ? '<div style="margin-top:10px;border-top:1px solid #000;padding-top:5px"><strong>Paiements</strong>' + paiements + '</div>' : ''}
      ${renderRefundSynthesisHtml(model)}
      ${renderArticlesRetournesHtml(model)}
      <div class="total">TOTAL VENTE INITIALE : ${totalStr}</div>
      <p style="text-align:center;font-size:11px">${footer}</p>
      </body></html>`;
}

/**
 * Impression same-document (Safari iPad / PWA) — ne pas utiliser d'iframe 0×0.
 */
export function triggerDuplicataWindowPrint() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const cleanup = () => {
    document.body.classList.remove(PRINT_BODY_CLASS);
    window.removeEventListener('afterprint', cleanup);
  };

  document.body.classList.add(PRINT_BODY_CLASS);

  try {
    window.addEventListener('afterprint', cleanup);
    window.print();
  } catch {
    cleanup();
  }

  window.setTimeout(() => {
    if (document.body.classList.contains(PRINT_BODY_CLASS)) cleanup();
  }, 60_000);
}

/** @deprecated iframe — conservé pour compat tests ; ne plus appeler en prod */
export function printDuplicataHtmlDocument(html) {
  triggerDuplicataWindowPrint();
  void html;
}
